import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

// Renderer riutilizzabile della relazione. Usato per l'Anteprima lato Ufficio (readOnly)
// e predisposto per la futura compilazione lato Lavoratore (values + onChange).
function FieldLabel({ f }) {
  return (
    <div className="mb-1.5">
      <span className="text-sm font-semibold text-slate-800">
        {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {f.help_text && <p className="text-xs text-slate-500 mt-0.5">{f.help_text}</p>}
    </div>
  );
}

function RelationField({ f, value, onChange, readOnly, invalid }) {
  const set = (v) => { if (!readOnly && onChange) onChange(f.field_id, v); };
  const disabled = readOnly;

  if (f.type === "section") {
    return (
      <div className="pt-2 border-b border-slate-200 pb-2" data-testid={`rel-field-${f.field_id}`}>
        <h3 className="font-heading text-lg font-bold text-slate-900">{f.label}</h3>
        {f.help_text && <p className="text-sm text-slate-500 mt-0.5">{f.help_text}</p>}
      </div>
    );
  }
  if (f.type === "info") {
    return (
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid={`rel-field-${f.field_id}`}>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{f.label}</p>
        {f.help_text && <p className="text-xs text-slate-500 mt-1">{f.help_text}</p>}
      </div>
    );
  }

  return (
    <div data-testid={`rel-field-${f.field_id}`} className={invalid ? "rounded-lg ring-1 ring-red-300 bg-red-50/40 p-3 -mx-1" : ""}>
      <FieldLabel f={f} />
      {f.type === "short_text" && (
        <Input value={value || ""} disabled={disabled} onChange={(e) => set(e.target.value)} placeholder="Risposta breve" />
      )}
      {f.type === "long_text" && (
        <Textarea value={value || ""} disabled={disabled} onChange={(e) => set(e.target.value)} placeholder="Risposta aperta" rows={3} />
      )}
      {f.type === "number" && (
        <Input type="number" value={value ?? ""} disabled={disabled} onChange={(e) => set(e.target.value)} placeholder="0" className="max-w-[200px]" />
      )}
      {f.type === "date" && (
        <Input type="date" value={value || ""} disabled={disabled} onChange={(e) => set(e.target.value)} className="max-w-[220px]" />
      )}
      {f.type === "yes_no" && (
        <RadioGroup value={value || ""} disabled={disabled} onValueChange={set} className="flex gap-6">
          {["Sì", "No"].map((o) => (
            <div key={o} className="flex items-center gap-2">
              <RadioGroupItem value={o} id={`${f.field_id}-${o}`} />
              <Label htmlFor={`${f.field_id}-${o}`} className="text-sm font-normal">{o}</Label>
            </div>
          ))}
        </RadioGroup>
      )}
      {f.type === "single_choice" && (
        <RadioGroup value={value || ""} disabled={disabled} onValueChange={set} className="space-y-1.5">
          {(f.options || []).map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <RadioGroupItem value={o} id={`${f.field_id}-${i}`} />
              <Label htmlFor={`${f.field_id}-${i}`} className="text-sm font-normal">{o}</Label>
            </div>
          ))}
        </RadioGroup>
      )}
      {f.type === "multi_choice" && (
        <div className="space-y-1.5">
          {(f.options || []).map((o, i) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o);
            return (
              <div key={i} className="flex items-center gap-2">
                <Checkbox checked={checked} disabled={disabled}
                  onCheckedChange={() => set(checked ? arr.filter((x) => x !== o) : [...arr, o])} id={`${f.field_id}-${i}`} />
                <Label htmlFor={`${f.field_id}-${i}`} className="text-sm font-normal">{o}</Label>
              </div>
            );
          })}
        </div>
      )}
      {f.type === "semi_open" && (() => {
        const sel = value && typeof value === "object" ? value : { choice: "", other: "" };
        return (
          <div className="space-y-1.5">
            <RadioGroup value={sel.choice || ""} disabled={disabled}
              onValueChange={(v) => set({ ...sel, choice: v })} className="space-y-1.5">
              {(f.options || []).map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <RadioGroupItem value={o} id={`${f.field_id}-${i}`} />
                  <Label htmlFor={`${f.field_id}-${i}`} className="text-sm font-normal">{o}</Label>
                </div>
              ))}
              {f.allow_other !== false && (
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="__other__" id={`${f.field_id}-other`} />
                  <Label htmlFor={`${f.field_id}-other`} className="text-sm font-normal">Altro</Label>
                </div>
              )}
            </RadioGroup>
            {f.allow_other !== false && sel.choice === "__other__" && (
              <Input value={sel.other || ""} disabled={disabled} placeholder="Specifica…"
                onChange={(e) => set({ ...sel, other: e.target.value })} className="mt-1" />
            )}
          </div>
        );
      })()}
      {f.type === "rating" && (() => {
        const min = f.rating_min ?? 1, max = f.rating_max ?? 5;
        const nums = []; for (let n = min; n <= max; n++) nums.push(n);
        return (
          <div>
            <div className="flex flex-wrap gap-2">
              {nums.map((n) => (
                <button type="button" key={n} disabled={disabled} onClick={() => set(n)}
                  className={`h-9 w-9 rounded-lg border text-sm font-semibold transition-colors ${String(value) === String(n) ? "bg-[#6D5FB8] text-white border-[#6D5FB8]" : "bg-white text-slate-700 border-slate-200 hover:border-[#8478C4]"} ${disabled ? "cursor-default" : ""}`}>
                  {n}
                </button>
              ))}
            </div>
            {(f.rating_min_label || f.rating_max_label) && (
              <div className="flex justify-between text-xs text-slate-500 mt-1 max-w-[280px]">
                <span>{f.rating_min_label}</span><span>{f.rating_max_label}</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export function RelationRenderer({ title, description, fields = [], values = {}, onChange, readOnly = true, invalidIds = [] }) {
  return (
    <div className="space-y-4" data-testid="relation-renderer">
      {(title || description) && (
        <div className="border-b border-slate-200 pb-3">
          {title && <h2 className="font-heading text-xl font-bold text-slate-900">{title}</h2>}
          {description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{description}</p>}
        </div>
      )}
      {fields.length === 0 && (
        <p className="text-sm text-slate-400 py-4 text-center" data-testid="relation-renderer-empty">Nessun campo nella relazione.</p>
      )}
      {fields.map((f) => (
        <RelationField key={f.field_id} f={f} value={values[f.field_id]} onChange={onChange} readOnly={readOnly} invalid={invalidIds.includes(f.field_id)} />
      ))}
    </div>
  );
}

export default RelationRenderer;
